import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useConfig } from '@lore/config';

/**
 * @typedef {Object} EventImage
 * @property {string} imageUrl
 * @property {number} eventId
 * @property {number} width
 * @property {number} height
 */

/**
 * Custom hook for the share image of an event.
 * GET /images/event/{eventId} — `image` is null while loading or when the
 * event has no saved image yet (404 is treated as "no image").
 * upsert() POSTs /images/upsert (with bypassUserScope=true, since
 * steward-ui users are rarely the event's creator) and refreshes the query
 * on success. Both calls use the `default` connection.
 *
 * @param {number} eventId
 * @returns {import('@tanstack/react-query').UseQueryResult & { image: EventImage|null, upsert: Function, upserting: boolean, upsertError: boolean }}
 */
export default function useEventImage(eventId) {
  const config = useConfig();
  const { apiRoot, headers } = config.connections.default;
  const queryClient = useQueryClient();

  const queryKey = ['images', 'event', eventId];

  const query = useQuery({
    queryKey,
    queryFn: () => {
      const url = `${apiRoot}/images/event/${eventId}`;
      return fetch(url, {
        method: 'GET',
        headers: headers(),
      }).then((res) => {
        if (res.status === 404) {
          return null;
        }
        if (!res.ok) {
          throw new Error('Failed to fetch the event image');
        }
        return res.json();
      });
    },
    enabled: Boolean(eventId),
  });

  const upsertMutation = useMutation({
    mutationFn: ({ imageUrl, width, height }) => {
      const url = `${apiRoot}/images/upsert?bypassUserScope=true`;
      return fetch(url, {
        method: 'POST',
        headers: {
          ...headers(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ imageUrl, eventId, width, height }),
      }).then((res) => {
        if (!res.ok) {
          throw new Error('Failed to save the event image');
        }
        return res.json();
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(queryKey);
    },
  });

  return {
    ...query,
    image: query.data ?? null,

    // Actions
    upsert: ({ imageUrl, width, height }) => upsertMutation.mutate({ imageUrl, width, height }),
    upserting: upsertMutation.isLoading,
    upsertError: upsertMutation.isError,
  };
}
